import { RefObject, useCallback, useEffect, useRef, useState } from 'react'

interface CKEditorToggleResult<T> {
    /** A flag indicating whether a CKEditor instance is currently active. */
    active: boolean
    /** The key of the currently active CKEditor instance, or null if none is active. */
    activeEditorKey: T | null
    /** A reference to the CKEditor instance. */
    placeholderRef: RefObject<HTMLDivElement>
    /** A function to activate a CKEditor instance by key. */
    activate: (key?: T) => void
    /** A function to deactivate the currently active CKEditor instance. */
    deactivate: () => void
}

/**
 * A hook to manage CKEditor activation and deactivation.
 *
 * Features:
 * - Activates CKEditor when clicking on a placeholder.
 * - Deactivates CKEditor when clicking outside or losing focus (e.g., pressing Tab).
 * - Ensures only one editor is active at a time.
 *
 * @template T - The type of the key used to identify active CKEditor instances.
 * @param {T | null} contentItemId - The key/id of the content item associated with the CKEditor.
 * @returns {CKEditorToggleResult<T>} An object containing the state and functions for managing CKEditor activation and deactivation.
 */
export const useCKEditorToggle = <T>(
    contentItemId: T | null
): CKEditorToggleResult<T> => {
    const [activeEditorKey, setActiveEditorKey] = useState<T | null>(null)
    const placeholderRef = useRef<HTMLDivElement>(null)

    const active = activeEditorKey === contentItemId

    // handler that Activates CKEditor using its key/id
    const activate = useCallback(
        (currentContentId?: T) =>
            setActiveEditorKey(currentContentId ?? contentItemId),
        [active, contentItemId]
    )

    // handler that Deactivates CKEditor when clicking outside or losing focus
    const deactivate = useCallback(() => setActiveEditorKey(null), [])

    /** registers event listeners to handle outside clicks and loss of focus */
    useEffect(() => {
        const controller = new AbortController()
        const signal = controller.signal

        const handleBlurOrClickOutside = (event: Event) => {
            if (
                placeholderRef.current &&
                !placeholderRef.current.contains(event.target as Node)
            ) {
                deactivate()
            }
        }

        if (activeEditorKey) {
            document.addEventListener('mousedown', handleBlurOrClickOutside, {
                signal,
            })
            document.addEventListener('focusin', handleBlurOrClickOutside, {
                signal,
            })
        } else {
            controller.abort()
        }

        return () => controller.abort()
    }, [activeEditorKey])

    return {
        active,
        activeEditorKey,
        placeholderRef,
        activate,
        deactivate,
    }
}
